/**
 * Exit visibility — D138 (`docs/design/the-descent-arc.md` §2).
 *
 * @remarks
 * An exit that declares `reveal` does not exist for the player until the
 * world has REVEALED it: movement calls it a wall, the map does not draw it,
 * and nothing names its destination. The truth of "revealed" is the world's
 * `revealedExitIds` ledger, never the live condition — the condition is read
 * by exactly one thing, the watcher below, which decides when to write the
 * ledger. That is what makes fire-once free (a revealed exit is never a
 * candidate again), what keeps a door you have seen from un-seeing when its
 * condition flips back, and what makes the reveal a fact a save carries
 * rather than one a restore recomputes.
 *
 * The watcher runs once per turn, for the ACTIVE room only, after the action
 * and the scheduler tick and before the narrative triggers: the reveal is
 * heard by the player standing there — in the turn that satisfied the
 * condition, or on arrival in a room whose condition already held — and it
 * costs no turn of its own.
 *
 * @packageDocumentation
 */

import type { ExitDefinition, RuntimeState, RuntimeTranscriptEntry } from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { evaluateCondition } from '../conditions/evaluate-condition.js';
import { stampNarrativeLines, type TranscriptCursor } from './transcript-cursor.js';

/**
 * Whether the player can see (move through, map, name) this exit right now:
 * every exit without `reveal` always; an exit with it once the ledger says so.
 *
 * @param exit - The exit
 * @param state - The live runtime state
 * @returns Whether the exit exists for the player
 */
export const isExitVisible = (exit: ExitDefinition, state: RuntimeState): boolean =>
	exit.reveal === undefined || state.world.revealedExitIds.includes(exit.exitId);

/** The exits of a room the player can see — the one filter every consumer applies. */
export const visibleExitsOf = (
	exits: readonly ExitDefinition[],
	state: RuntimeState,
): readonly ExitDefinition[] => exits.filter((exit) => isExitVisible(exit, state));

/** What the reveal watcher did this turn. */
export interface RevealWatchResult {
	readonly cursor: TranscriptCursor;
	/** The reveal narratives, in exit order, after whatever the turn already said. */
	readonly entries: readonly RuntimeTranscriptEntry[];
	readonly state: RuntimeState;
}

/**
 * Reveal every hidden exit of the active room whose condition now holds —
 * once each, in the room's authored exit order — speaking its narratives
 * under the `exit_revealed:<exitId>` context.
 *
 * @param bundle - The loaded bundle
 * @param state - The post-action, post-tick state
 * @param cursor - Where the reveal's lines land
 * @returns The state with the ledger extended, and the lines spoken
 */
export const revealExits = (bundle: EngineGameBundle, state: RuntimeState, cursor: TranscriptCursor): RevealWatchResult => {
	const activeRoom = bundle.indexes.roomsById.get(state.progression.activeRoomId);
	const entries: RuntimeTranscriptEntry[] = [];
	let workingState = state;
	let workingCursor = cursor;

	/*
	 * A room the pack does not define reveals nothing — the same defensive
	 * posture as every other walk over a state a corrupt save could carry.
	 *
	 * It is also unreachable from a session that loaded: the active room id
	 * comes from the pack's own graph, and a save carrying one that does not
	 * resolve is refused before a turn runs. The guard stays because the cost
	 * of being wrong about that is a thrown turn on a player's save, and the
	 * cost of keeping it is this comment.
	 */
	/* v8 ignore next 3 */
	const exits = activeRoom === undefined ? [] : activeRoom.exits;

	for (const exit of exits) {
		const { reveal } = exit;

		if (reveal === undefined || isExitVisible(exit, workingState) || !evaluateCondition(reveal.when, bundle, workingState)) {
			continue;
		}

		workingState = {
			...workingState,
			world: { ...workingState.world, revealedExitIds: [...workingState.world.revealedExitIds, exit.exitId]},
		};

		const stamped = stampNarrativeLines(bundle, workingState, workingCursor, reveal.narratives, `exit_revealed:${exit.exitId}`);

		entries.push(...stamped.entries);
		workingCursor = stamped.cursor;
	}

	return { cursor: workingCursor, entries, state: workingState };
};
